'use client';

import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/utils';
import { Loader2, Lock, Shield, Building2, CreditCard, Wallet } from 'lucide-react';
import { showNotification } from '@/lib/generalNotifications';
import { showTransactionSuccess } from '@/lib/notifications';
import type { Invoice, PaymentMethod } from '@/types/firebase';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { getApiBaseUrl } from '@/lib/environment';

// Schema for ACH payment validation
const achPaymentSchema = z.object({
  accountName: z.string().min(1, 'Account name is required'),
  routingNumber: z.string()
    .min(9, 'Routing number must be 9 digits')
    .max(9, 'Routing number must be 9 digits')
    .regex(/^\d{9}$/, 'Routing number must contain only numbers'),
  accountNumber: z.string()
    .min(4, 'Account number must be at least 4 digits')
    .max(17, 'Account number must be at most 17 digits')
    .regex(/^\d+$/, 'Account number must contain only numbers'),
  accountType: z.enum(['checking', 'savings'], {
    required_error: 'Account type is required',
  }),
  savePaymentMethod: z.boolean().default(true),
});

type ACHPaymentFormValues = z.infer<typeof achPaymentSchema>;

interface ACHPaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoice: Partial<Invoice> & { 
    id: string;
    // Add individual address fields that might be available
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    // Add donation-specific fields
    country?: string;
    dynamicFields?: Record<string, string>;
  };
  invoiceAmount: number;
  onPaymentSuccess?: (transactionId: string, cardLast4?: string, paymentMethod?: string) => void;
}

export function ACHPaymentModal({
  open,
  onOpenChange,
  invoice,
  invoiceAmount,
  onPaymentSuccess
}: ACHPaymentModalProps) {
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'new_ach' | 'saved_ach'>('new_ach');
  const [savedPaymentMethods, setSavedPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedSavedMethod, setSelectedSavedMethod] = useState<PaymentMethod | null>(null);
  const isRecurringOrInstallment = invoice.invoiceType === 'recurring' || invoice.invoiceType === 'installment';
  
  // Check if this is a donation payment
  const isDonation = invoice.id.startsWith('donation-');

  const form = useForm<ACHPaymentFormValues>({
    resolver: zodResolver(achPaymentSchema),
    defaultValues: {
      accountName: '',
      routingNumber: '',
      accountNumber: '',
      accountType: 'checking',
      savePaymentMethod: true,
    },
  });

  // Load saved ACH payment methods when modal opens
  useEffect(() => {
    if (open && invoice.customerId && invoice.companyId) {
      loadSavedPaymentMethods();
    }
  }, [open, invoice.customerId, invoice.companyId]);

  const loadSavedPaymentMethods = async () => {
    if (!invoice.customerId || !invoice.companyId) return;
    
    try {
      console.log('Loading saved ACH payment methods for:', {
        customerId: invoice.customerId,
        companyId: invoice.companyId
      });
      
      const { db } = await import('@/lib/firebase');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const paymentMethodsRef = collection(db, 'paymentMethods');
      const q = query(
        paymentMethodsRef,
        where('customerId', '==', invoice.customerId),
        where('companyId', '==', invoice.companyId),
        where('type', '==', 'ach')
      );
      
      const snapshot = await getDocs(q);
      console.log(`Found ${snapshot.size} ACH payment methods`);
      
      if (!snapshot.empty) {
        const methods = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as PaymentMethod));
        
        console.log('ACH payment methods found:', methods);
        setSavedPaymentMethods(methods);
        
        // Auto-select the first (default) saved method if available
        const defaultMethod = methods.find(m => m.isDefault) || methods[0];
        setSelectedSavedMethod(defaultMethod);
        
        // If there are saved methods, default to using saved method
        setPaymentMethod('saved_ach');
      } else {
        console.log('No ACH payment methods found, defaulting to new payment');
        setSavedPaymentMethods([]);
        setPaymentMethod('new_ach');
      }
    } catch (error) {
      console.error('Error loading saved payment methods:', error);
      setSavedPaymentMethods([]);
      setPaymentMethod('new_ach');
    }
  };

  const handleSubmit = async (values: ACHPaymentFormValues) => {
    setIsProcessing(true);

    try {
      // Both donations and regular invoices now use the same ACH endpoint on the backend
      const apiBaseUrl = getApiBaseUrl();
      console.log('Using API base URL for ACH payment:', apiBaseUrl, '(isDonation:', isDonation, ')');

      // Get customer name from invoice if available
      const customerNameParts = invoice.customerName ? invoice.customerName.split(' ') : ['', ''];
      const firstName = customerNameParts[0] || '';
      const lastName = customerNameParts.slice(1).join(' ') || '';

      // Initialize billing address variables with empty strings
      let street = '';
      let city = '';
      let state = '';
      let zip = '';

      // Extract address from invoice if available - using direct fields
      if (invoice) {
        street = invoice.street || '';
        city = invoice.city || '';
        state = invoice.state || '';
        zip = invoice.zip || '';
      }

      // Create payment data based on whether this is a donation or regular invoice
      let paymentData;
      let apiEndpoint;
      
      if (isDonation) {
        // For donations, use the same ACH endpoint but with donation-specific data
        apiEndpoint = '/api/nmi/ach/process';
        const donationDescription =
          typeof (invoice as any).paymentDescription === 'string'
            ? (invoice as any).paymentDescription.trim()
            : '';
        paymentData = {
          type: 'sale',
          payment: 'check',
          checkname: values.accountName,
          checkaba: values.routingNumber,
          checkaccount: values.accountNumber,
          account_type: values.accountType,
          amount: invoiceAmount.toFixed(2),
          invoiceId: invoice.id,
          customerId: invoice.customerId,
          orderid: invoice.invoiceNumber,
          orderdescription: donationDescription || `Donation payment - ${invoice.invoiceNumber}`,
          savePaymentMethod: values.savePaymentMethod,
          companyId: invoice.companyId,
          salesTax: 0, // Donations typically don't have tax
          // Add customer name information to appear in NMI gateway
          customer_name: values.accountName,
          first_name: firstName,
          last_name: lastName,
          email: invoice.customerEmail || '',
          // Add billing address information
          address1: street || '',
          city: city || '',
          state: state || '',
          zip: zip || '',
          // Add donation-specific metadata
          isDonation: true,
          dynamicFields: (invoice as any).dynamicFields || {},
          metadata: {
            source: 'ach_payment_modal',
            invoiceId: invoice.id,
            isDonation: true,
            donationDescription
          }
        };
      } else {
        // Regular invoice payment
        apiEndpoint = '/api/nmi/ach/process';
        paymentData = {
          type: 'sale',
          payment: 'check',
          checkname: values.accountName,
          checkaba: values.routingNumber,
          checkaccount: values.accountNumber,
          account_type: values.accountType,
          amount: invoiceAmount.toFixed(2),
          invoiceId: invoice.id,
          customerId: invoice.customerId,
          orderid: invoice.invoiceNumber,
          orderdescription: `Payment for invoice #${invoice.invoiceNumber}`,
          savePaymentMethod: values.savePaymentMethod,
          companyId: invoice.companyId,
          salesTax: invoice.taxAmount || 0,
          // Add customer name information to appear in NMI gateway
          customer_name: values.accountName,
          first_name: firstName,
          last_name: lastName,
          email: invoice.customerEmail || '',
          // Add billing address information - make sure address1 is set correctly
          address1: street || '',
          city: city || '',
          state: state || '',
          zip: zip || ''
        };
      }

      console.log('Sending ACH payment', {
        isDonation,
        endpoint: apiEndpoint,
        accountName: values.accountName,
        customerName: `${firstName} ${lastName}`.trim(),
        address: street || '[empty]',
        addressInfo: `${city || ''}, ${state || ''} ${zip || ''}`
      });

      // Send request to the appropriate API endpoint
      const response = await fetch(`${apiBaseUrl}${apiEndpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const result = await response.json();

      if (result.success) {
        // Invalidate React Query caches to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['transactions'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });

        // Show a confirmation dialog and only proceed when the user clicks Done
        showTransactionSuccess({
          transactionid: result.transactionId,
          amount: invoiceAmount,
          cardLast4: values.accountNumber.slice(-4),
          onClose: () => {
            // Close modal and notify parent after user acknowledges
            onOpenChange(false);
            if (onPaymentSuccess) {
              onPaymentSuccess(result.transactionId, values.accountNumber.slice(-4), 'ach');
            }
          }
        });
      } else {
        // Show error notification
        showNotification({
          title: "Payment Failed",
          message: result.message || "An error occurred during payment processing",
          type: "error"
        });
      }
    } catch (error) {
      console.error('ACH payment error:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "An unexpected error occurred",
        type: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSavedPaymentSubmit = async () => {
    if (!selectedSavedMethod) {
      showNotification({
        title: "Error",
        message: "No saved payment method selected",
        type: "error"
      });
      return;
    }

    // Check if the saved method has the required nmiCustomerId
    // If missing, try to regenerate it based on the customer ID
    let customerVaultId = selectedSavedMethod.nmiCustomerId;
    if (!customerVaultId && invoice.customerId) {
      // Generate the expected vault ID for ACH payments
      const sanitizedBase = invoice.customerId.toString().replace(/[^a-zA-Z0-9]/g, '');
      customerVaultId = `cust_${sanitizedBase}_ACH`;
      console.log(`ACH Payment: Generated fallback vault ID: ${customerVaultId}`);
    }

    if (!customerVaultId) {
      showNotification({
        title: "Error",
        message: "This saved payment method is missing required information. Please use a new payment method.",
        type: "error"
      });
      return;
    }

    setIsProcessing(true);

    try {
      // Both donations and regular invoices use the same ACH endpoint on the backend
      const apiBaseUrl = getApiBaseUrl();
      console.log('Using API base URL for saved ACH payment:', apiBaseUrl, '(isDonation:', isDonation, ')');

      // Get customer name from invoice if available
      const customerNameParts = invoice.customerName ? invoice.customerName.split(' ') : ['', ''];
      const firstName = customerNameParts[0] || '';
      const lastName = customerNameParts.slice(1).join(' ') || '';

      // Initialize billing address variables with empty strings
      let street = '';
      let city = '';
      let state = '';
      let zip = '';

      // Extract address from invoice if available - using direct fields
      if (invoice) {
        street = invoice.street || '';
        city = invoice.city || '';
        state = invoice.state || '';
        zip = invoice.zip || '';
      }

      // Use saved ACH payment method
      const paymentData = {
        type: 'sale',
        payment: 'check',
        customer_vault_id: customerVaultId,
        amount: invoiceAmount.toFixed(2),
        invoiceId: invoice.id,
        customerId: invoice.customerId,
        orderid: invoice.invoiceNumber,
        orderdescription: `Payment for invoice #${invoice.invoiceNumber}`,
        companyId: invoice.companyId,
        salesTax: invoice.taxAmount || 0,
        // Add customer name information to appear in NMI gateway
        first_name: firstName,
        last_name: lastName,
        email: invoice.customerEmail || '',
        // Add billing address information
        address1: street || '',
        city: city || '',
        state: state || '',
        zip: zip || ''
      };

      console.log('Processing payment with saved ACH method:', {
        customerVaultId: customerVaultId,
        paymentMethodType: selectedSavedMethod.accountType,
        last4: selectedSavedMethod.last4,
        usingFallbackVaultId: !selectedSavedMethod.nmiCustomerId
      });

      // Send request to your API endpoint for ACH processing
      const response = await fetch(`${apiBaseUrl}/api/nmi/ach/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(paymentData),
      });

      const result = await response.json();

      if (result.success) {
        // If we used a fallback vault ID and the payment was successful,
        // update the payment method record with the correct vault ID
        if (!selectedSavedMethod.nmiCustomerId && invoice.customerId) {
          try {
            const { doc, updateDoc } = await import('firebase/firestore');
            const { db } = await import('@/lib/firebase');
            
            await updateDoc(doc(db, 'paymentMethods', selectedSavedMethod.id), {
              nmiCustomerId: customerVaultId,
              updatedAt: new Date(),
              lastUsedAt: new Date()
            });
            
            console.log('Updated payment method with vault ID:', customerVaultId);
          } catch (updateError) {
            console.error('Error updating payment method with vault ID:', updateError);
            // Don't fail the payment if the update fails
          }
        }

        // Invalidate React Query caches to ensure fresh data
        await queryClient.invalidateQueries({ queryKey: ['invoices'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['transactions'], exact: false });
        await queryClient.invalidateQueries({ queryKey: ['invoice', invoice.id] });

        // Show a confirmation dialog and only proceed when the user clicks Done
        showTransactionSuccess({
          transactionid: result.transactionId,
          amount: invoiceAmount,
          cardLast4: selectedSavedMethod.last4,
          onClose: () => {
            onOpenChange(false);
            if (onPaymentSuccess) {
              onPaymentSuccess(result.transactionId, selectedSavedMethod.last4, 'ach');
            }
          }
        });
      } else {
        // Show error notification
        showNotification({
          title: "Payment Failed",
          message: result.message || "An error occurred during payment processing",
          type: "error"
        });
      }
    } catch (error) {
      console.error('Saved ACH payment error:', error);
      showNotification({
        title: "Error",
        message: error instanceof Error ? error.message : "An unexpected error occurred",
        type: "error"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="sm:max-w-md p-0 overflow-hidden rounded-xl border-0 shadow-2xl"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-r from-green-600 to-green-700 p-6 text-white">
          <DialogHeader className="mb-2">
            <DialogTitle className="text-2xl font-bold text-white">Pay Invoice #{invoice.invoiceNumber}</DialogTitle>
          </DialogHeader>
        </div>
        
        <div className="px-6 py-5 bg-white">
          <div className="flex justify-between mb-6 pb-4 border-b border-gray-100">
            <div>
              <Label className="text-sm text-gray-500 font-medium">Total Amount</Label>
              <div className="text-xl font-semibold">{formatCurrency(invoiceAmount)}</div>
            </div>
            
            <div>
              <Label className="text-sm text-gray-500 font-medium">Processing Fee</Label>
              <div className="text-xl font-semibold text-green-600">$0.00</div>
            </div>
          </div>
          
          <div className="space-y-4 mb-6">
            {/* Payment Method Selection */}
            {savedPaymentMethods.length > 0 && (
              <div className="space-y-4">
                <Label className="block text-base font-semibold text-gray-700">
                  Choose Payment Method
                </Label>
                
                <RadioGroup value={paymentMethod} onValueChange={(value) => setPaymentMethod(value as 'new_ach' | 'saved_ach')}>
                  <div className="grid gap-4">
                    {/* Saved ACH Method Option */}
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="saved_ach" id="saved_ach" />
                      <Label htmlFor="saved_ach" className="flex-1 cursor-pointer">
                        <Card className={`p-4 transition-colors ${paymentMethod === 'saved_ach' ? 'border-green-500 bg-green-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <Wallet className="h-5 w-5 text-green-600" />
                            <div>
                              <div className="font-medium">Saved Bank Account</div>
                              {selectedSavedMethod && (
                                <div className="text-sm text-gray-600">
                                  {selectedSavedMethod.accountType} •••• {selectedSavedMethod.last4}
                                  {selectedSavedMethod.routingNumberLast4 && 
                                    ` (Routing: •••• ${selectedSavedMethod.routingNumberLast4})`
                                  }
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      </Label>
                    </div>
                    
                    {/* New ACH Method Option */}
                    <div className="flex items-center space-x-3">
                      <RadioGroupItem value="new_ach" id="new_ach" />
                      <Label htmlFor="new_ach" className="flex-1 cursor-pointer">
                        <Card className={`p-4 transition-colors ${paymentMethod === 'new_ach' ? 'border-green-500 bg-green-50' : 'hover:bg-gray-50'}`}>
                          <div className="flex items-center gap-3">
                            <CreditCard className="h-5 w-5 text-green-600" />
                            <div>
                              <div className="font-medium">Use a New Bank Account</div>
                              <div className="text-sm text-gray-600">Enter new bank account details</div>
                            </div>
                          </div>
                        </Card>
                      </Label>
                    </div>
                  </div>
                </RadioGroup>
              </div>
            )}
            
            <div className="flex items-center justify-between mb-4">
              <Label className="block text-base font-semibold text-gray-700">
                {paymentMethod === 'saved_ach' && selectedSavedMethod ? 'Payment Details' : 'Bank Account Information'}
              </Label>
              <div className="flex items-center text-sm text-gray-500">
                <Lock size={14} className="mr-1" />
                <span>Secure Payment</span>
              </div>
            </div>
            
            {paymentMethod === 'saved_ach' && selectedSavedMethod ? (
              // Show saved payment method details
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Wallet className="h-5 w-5 text-green-600" />
                    <div className="font-medium text-green-800">Saved Bank Account</div>
                  </div>
                  <div className="space-y-2 text-sm text-green-700">
                    <div>Account Type: {selectedSavedMethod.accountType}</div>
                    <div>Account: •••• {selectedSavedMethod.last4}</div>
                    {selectedSavedMethod.routingNumberLast4 && (
                      <div>Routing: •••• {selectedSavedMethod.routingNumberLast4}</div>
                    )}
                  </div>
                </div>
                
                <Button
                  onClick={handleSavedPaymentSubmit}
                  disabled={isProcessing}
                  className="w-full h-12 text-base font-medium rounded-lg bg-green-600 hover:bg-green-700 transition-colors"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Building2 className="mr-2 h-5 w-5" />
                      Pay {formatCurrency(invoiceAmount)}
                    </>
                  )}
                </Button>
              </div>
            ) : (
              // Show new payment method form
              <Form {...form}>
                <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="accountName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Holder Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John Smith" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="routingNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Routing Number</FormLabel>
                        <FormControl>
                          <Input placeholder="123456789" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="accountNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Number</FormLabel>
                        <FormControl>
                          <Input placeholder="1234567890" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="accountType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Account Type</FormLabel>
                        <FormControl>
                          <div className="flex gap-4">
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                id="checking"
                                value="checking"
                                checked={field.value === "checking"}
                                onChange={() => field.onChange("checking")}
                                className="h-4 w-4 text-green-600"
                              />
                              <Label htmlFor="checking">Checking</Label>
                            </div>
                            <div className="flex items-center space-x-2">
                              <input
                                type="radio"
                                id="savings"
                                value="savings"
                                checked={field.value === "savings"}
                                onChange={() => field.onChange("savings")}
                                className="h-4 w-4 text-green-600"
                              />
                              <Label htmlFor="savings">Savings</Label>
                            </div>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="savePaymentMethod"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 mt-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            className="text-green-600 border-green-500"
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="text-sm text-gray-600">
                            {isRecurringOrInstallment
                              ? `Save payment method for future ${invoice.invoiceType} payments`
                              : 'Save payment method for future payments'}
                          </FormLabel>
                          {isRecurringOrInstallment && (
                            <p className="text-xs text-green-700">
                              By checking this box, you authorize us to store your payment information securely and to automatically 
                              charge this payment method for future {invoice.invoiceType === 'recurring' ? 'recurring' : 'installment'} payments 
                              according to the schedule outlined in your invoice.
                            </p>
                          )}
                        </div>
                      </FormItem>
                    )}
                  />
                  
                  <Button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full h-12 text-base font-medium rounded-lg bg-green-600 hover:bg-green-700 transition-colors mt-4"
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Building2 className="mr-2 h-5 w-5" />
                        Pay {formatCurrency(invoiceAmount)}
                      </>
                    )}
                  </Button>
                                 </form>
               </Form>
             )}
             
             <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-500">
               <Shield size={14} />
               <span>Payments are securely processed and encrypted</span>
             </div>
           </div>
         </div>
       </DialogContent>
     </Dialog>
   );
 } 
